import { useEffect, useMemo, useRef, useState } from "react";
import { CheckCircleOutlined, CloseCircleOutlined, CommentOutlined, LoadingOutlined } from "@ant-design/icons";
import { addCommentRequest } from "store/comments/actions";
import { buildToolError } from "./errorEnvelope";
import type { ToolPartProps, UserSummary } from "./types";

interface AddCommentInput {
    content: string;
    description: string;
}

interface AddCommentOutput {
    success: boolean;
    cancelled?: boolean;
    error?: string;
    taggedUserCount?: number;
}

type Phase = "ready" | "denied" | "applying" | "applied" | "error";

function resolveTaggedUsers(content: string, users: UserSummary[]): string[] {
    const nameToId: Record<string, string> = {};
    for (const user of users) {
        if (!user.active || user.deleted) continue;
        const fullName = `${user.firstName || ""} ${user.lastName || ""}`.trim();
        if (fullName) nameToId[fullName] = user.userId;
    }
    const taggedUserIds = new Set<string>();
    for (const fullName in nameToId) {
        const mentionRegex = new RegExp(`@${fullName.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}\\b`, "g");
        if (content.match(mentionRegex)) {
            taggedUserIds.add(nameToId[fullName]);
        }
    }
    return Array.from(taggedUserIds);
}

export default function AddCommentTool({ toolCallId, state, input, output, errorText, helpers }: ToolPartProps<AddCommentInput, AddCommentOutput>) {
    const { dispatch, addToolOutput, dataSet, commentPermission, users, addCommentResult } = helpers;

    const [phase, setPhase] = useState<Phase>("ready");
    const [denyReason, setDenyReason] = useState<string | null>(null);
    const initialisedRef = useRef(false);
    const resolvedRef = useRef(false);
    const resultAtDispatchRef = useRef<unknown>(null);

    const taggedUsers = useMemo(() => resolveTaggedUsers(input?.content || "", users || []), [input?.content, users]);

    useEffect(() => {
        if (initialisedRef.current) return;
        if (state !== "input-available") return;
        initialisedRef.current = true;
        if (!commentPermission?.canAdd) {
            setPhase("denied");
            setDenyReason("You don't have permission to add comments to this data set.");
            return;
        }
        if (!dataSet?.dataSetId) {
            setPhase("denied");
            setDenyReason("Data set context is incomplete.");
            return;
        }
    }, [state, commentPermission, dataSet]);

    useEffect(() => {
        if (resolvedRef.current) return;
        if (state === "output-available" || state === "output-error") return;
        if (phase === "denied") {
            resolvedRef.current = true;
            addToolOutput({ tool: "addComment", toolCallId, output: { success: false, error: denyReason || "Cannot add comment." } });
        }
    }, [phase, denyReason, state, toolCallId, addToolOutput]);

    useEffect(() => {
        if (phase !== "applying") return;
        if (!addCommentResult) return;
        if (addCommentResult === resultAtDispatchRef.current) return;
        if (addCommentResult.success === true) {
            setPhase("applied");
            if (!resolvedRef.current) {
                resolvedRef.current = true;
                addToolOutput({
                    tool: "addComment",
                    toolCallId,
                    output: { success: true, taggedUserCount: taggedUsers.length },
                });
            }
        } else if (addCommentResult.error) {
            setPhase("error");
            if (!resolvedRef.current) {
                resolvedRef.current = true;
                addToolOutput({
                    tool: "addComment",
                    toolCallId,
                    output: buildToolError(addCommentResult.error, "Failed to add comment."),
                });
            }
        }
    }, [addCommentResult, phase, toolCallId, addToolOutput, taggedUsers.length]);

    const handleAdd = () => {
        if (!dataSet?.dataSetId) return;
        const content = (input?.content || "").trim();
        if (!content) {
            setPhase("error");
            if (!resolvedRef.current) {
                resolvedRef.current = true;
                addToolOutput({ tool: "addComment", toolCallId, output: { success: false, error: "Comment content is empty." } });
            }
            return;
        }
        const commentData = {
            content,
            taggedUsers,
            recordType: "dataset",
            redirect_url: typeof window !== "undefined" ? window.location.href : null,
        };
        resultAtDispatchRef.current = addCommentResult;
        setPhase("applying");
        dispatch(addCommentRequest(dataSet.dataSetId, commentData));
    };

    const handleCancel = () => {
        if (!resolvedRef.current) {
            resolvedRef.current = true;
            addToolOutput({ tool: "addComment", toolCallId, output: { success: false, cancelled: true } });
        }
    };

    return (
        <div className="rounded-2xl border border-[#001D3D]/40 bg-white p-3 text-sm text-zinc-800 shadow-sm">
            <div className="mb-2 flex items-center gap-2 text-[#001D3D]">
                <CommentOutlined />
                <span className="text-xs font-semibold uppercase tracking-wider">Add comment</span>
            </div>
            {input?.description && <div className="text-xs text-zinc-600">{input.description}</div>}

            {phase === "denied" && (
                <div className="mt-3 flex items-center gap-2 rounded-md border border-red-200 bg-red-50 px-2 py-1 text-xs text-red-700">
                    <CloseCircleOutlined /> {denyReason}
                </div>
            )}

            {phase !== "denied" && input?.content && (
                <div className="mt-2 whitespace-pre-wrap rounded-md border border-zinc-200 bg-zinc-50 px-2 py-2 text-xs text-zinc-900">{input.content}</div>
            )}
            {phase !== "denied" && taggedUsers.length > 0 && (
                <div className="mt-1 text-[10px] text-zinc-500">
                    {taggedUsers.length} user{taggedUsers.length === 1 ? "" : "s"} will be notified.
                </div>
            )}

            {phase === "applied" && (
                <div className="mt-3 flex items-center gap-2 rounded-md border border-green-200 bg-green-50 px-2 py-1 text-xs text-green-700">
                    <CheckCircleOutlined /> Comment added.
                </div>
            )}
            {phase === "error" && (
                <div className="mt-3 flex items-center gap-2 rounded-md border border-red-200 bg-red-50 px-2 py-1 text-xs text-red-700">
                    <CloseCircleOutlined /> Failed to add comment.
                </div>
            )}
            {state === "output-available" && output?.cancelled && (
                <div className="mt-3 flex items-center gap-2 rounded-md border border-zinc-200 bg-zinc-50 px-2 py-1 text-xs text-zinc-600">
                    <CloseCircleOutlined /> Cancelled.
                </div>
            )}
            {state === "output-error" && (
                <div className="mt-3 flex items-center gap-2 rounded-md border border-red-200 bg-red-50 px-2 py-1 text-xs text-red-700">
                    <CloseCircleOutlined /> {errorText || "Tool error."}
                </div>
            )}

            {phase === "ready" && state !== "output-available" && state !== "output-error" && (
                <div className="mt-3 flex items-center justify-end gap-2">
                    <button type="button" onClick={handleCancel} className="rounded-md border border-zinc-300 bg-white px-3 py-1 text-xs text-zinc-700 hover:bg-zinc-50">
                        Cancel
                    </button>
                    <button type="button" onClick={handleAdd} className="rounded-md bg-[#001D3D] px-3 py-1 text-xs font-medium text-white hover:bg-[#002D5C]">
                        Add
                    </button>
                </div>
            )}
            {phase === "applying" && (
                <div className="mt-3 flex items-center justify-end gap-2 text-xs text-zinc-500">
                    <LoadingOutlined spin /> Adding...
                </div>
            )}
        </div>
    );
}
